/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  EXECUTION_TYPE,
  UiController,
  breakdownList,
  consoleCommands,
  events,
} from 'modules/common/ui-controller';
import {
  expandFocusOnAll,
  getHotelValuesFromList,
  getValuesFromList,
  isIncluded,
} from 'modules/common/utils/array';
import { PACE_HORIZON_SELECTION } from 'modules/dashboard/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import {
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
} from 'modules/common/ui-controller/utils/shared-command-steps';
import { paceActions } from '../slice';
import { setCompareWindowCommandValue } from '../../inner-filter/functions';

import PACE_COLLAPSE_COMMAND_STEPS from './pace-collapse-command-steps';

const isLoaded = (context) => context.isLoaded;
// focus on command with the EMPTY value clears the focus on selection
const isEmptyFocusOn = (value) => value?.length === 1 && value[0] === EXECUTION_TYPE.EMPTY;
/**
 * Pace tab filter commands in the order they are applied.
 * The position in this list is the execution order, see useCommandPipeline.
 * context is built in usePaceCommandPipeline.
 */
const PACE_FILTER_COMMAND_STEPS = [
  {
    command: consoleCommands.LAG_ALIGN,
    event: events.SET_PACE_LAG_ALIGNED,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(paceActions.setPaceLagAligned(value === 1));
    },
  },
  {
    command: consoleCommands.CUMULATIVE_VIEW,
    event: events.SET_PACE_CUMULATIVE_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(paceActions.setPaceCumulativeView(value === 1));
    },
  },
  {
    command: consoleCommands.SEGMENT,
    event: events.SET_PACE_BREAKDOWN,
    ready: (context) => context.isLoaded && context.chartBreakdownList.length > 0,
    apply: (value, { dispatch, chartBreakdownList }) => {
      // get breakdown option
      const breakdownOption = breakdownList[value?.substring(0, 2)];
      // get isDetailed state from command value
      const detailsStatus = parseInt(value?.substring(2, 4), 10);
      if (breakdownOption !== undefined) {
        const option = chartBreakdownList?.filter((val) => val?.label?.includes(breakdownOption));
        if (option?.length > 0) {
          dispatch(paceActions.setPaceChartBreakdown(option[0]?.id));
          dispatch(paceActions.setPaceFocusOn([]));
        }
        // set isDetailed  state
        if (!Number.isNaN(detailsStatus)) {
          dispatch(paceActions.setIsPaceDetailed(detailsStatus === 1));
          dispatch(paceActions.setPaceFocusOn([]));
        }
      }
    },
  },
  {
    // booking pace window decides which one is applied: booking date (BPW) or pace horizon (BPH)
    command: consoleCommands.BOOKING_PACE_WINDOW,
    requires: [consoleCommands.BOOKING_DATE, consoleCommands.PACE_HORIZON],
    event: events.SET_PACE_BOOKING_PACE_WINDOW,
    ready: isLoaded,
    apply: (value, { dispatch, resetBookingDate }) => {
      if (value === PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON) {
        dispatch(paceActions.setPaceBookingPaceWindow(PACE_HORIZON_SELECTION.BOOKING_PACE_HORIZON));
        dispatch(paceActions.setPaceBookingDate(resetBookingDate));
        return [consoleCommands.BOOKING_PACE_WINDOW, consoleCommands.BOOKING_DATE];
      }
      // booking date is applied by the next step once the booking pace window is selected,
      // pace horizon is not used with the booking pace window
      dispatch(paceActions.setPaceBookingPaceWindow(PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW));
      return [consoleCommands.BOOKING_PACE_WINDOW, consoleCommands.PACE_HORIZON];
    },
  },
  {
    command: consoleCommands.BOOKING_DATE,
    event: events.SET_PACE_CUSTOM_BOOKING,
    ready: (context, { keys }) =>
      context.isLoaded &&
      (context.paceWindowSelection === PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW ||
        !keys.includes(consoleCommands.BOOKING_PACE_WINDOW)),
    apply: (value, { paceWindowSelection }) => {
      if (paceWindowSelection === PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW) {
        // emit set custom booking date range event
        UiController.setBookingDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.PACE_HORIZON,
    event: events.SET_PACE_PACE_HORIZON,
    ready: (context) => context.isLoaded && context.paceList?.length > 0,
    apply: (value, { dispatch, paceList }) => {
      // get pace horizon value
      const paces = paceList?.filter((pace) => pace?.label[0].includes(value));
      if (paces?.length > 0) {
        dispatch(dashboardActions.setPaceHorizon(paces[0]?.id));
      }
    },
  },
  {
    command: consoleCommands.COMPARE_HOTEL,
    event: events.SET_PACE_COMPARE_HOTELS,
    ready: (context) => context.isLoaded && context.hotelList?.length > 0,
    apply: (value, { dispatch, hotelList }) => {
      const hotelsSelected = getHotelValuesFromList(value, hotelList);
      if (hotelsSelected?.length > 0) {
        dispatch(paceActions.setEnableSecondaryHotelGroups(false));
        dispatch(paceActions.setHotelFocusOn(hotelsSelected[0]));
      }
    },
  },
  {
    command: consoleCommands.COMPARE_HOTEL_GROUP,
    event: events.SET_PACE_HOTEL_GROUP,
    ready: (context) => context.isLoaded && context.hotelGroupList?.length > 0,
    apply: (value, { dispatch, hotelGroupList }) => {
      const hotelGroupsSelected = getHotelValuesFromList(value, hotelGroupList);
      if (hotelGroupsSelected?.length > 0) {
        dispatch(paceActions.setEnableSecondaryHotelGroups(true));
        dispatch(paceActions.setHotelFocusOn(hotelGroupsSelected[0]));
      }
    },
  },
  {
    command: consoleCommands.NORMALIZED_VIEW,
    event: events.SET_PACE_NORMALIZED_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(paceActions.setNormalizedView(value === 1));
    },
  },
  {
    command: consoleCommands.ALIGNMENT,
    event: events.PACE_ALIGNMENT,
    ready: (context) => context.isLoaded && Boolean(context.visualFilters),
    apply: (value, { handleToggleChange }) => {
      // set alignment toggle value
      handleToggleChange({ target: { checked: value } });
    },
  },
  {
    command: consoleCommands.SYMMETRIC,
    event: events.SET_PACE_SYMMETRIC_COMPARISON,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(paceActions.setPaceSymmetricComparisonToggle(value === 1));
    },
  },
  {
    command: consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE,
    event: events.SET_PACE_SYMMETRIC_COMPARISON_COLLAPSE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(paceActions.setSymmetricComparisonIsDisplay(value === 1));
    },
  },
  {
    command: consoleCommands.BREAKDOWN_TOGGLE,
    event: events.SET_PACE_BREAKDOWN_TOGGLE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(paceActions.setIsPrimary(value === 1));
    },
  },
  {
    command: consoleCommands.FOCUS,
    event: events.SET_PACE_FOCUS_ON,
    // wait until the focus on list belongs to the selected breakdown and contains the command values
    ready: (context, { pending }) => {
      const value = pending[consoleCommands.FOCUS];
      if (!context.isLoaded || !context.isFocusOnListReady || !(context.focusOnList.length > 0)) {
        return false;
      }
      if (isEmptyFocusOn(value)) return true;
      // replace ALL command value with every focus on option
      const focusOnValues = expandFocusOnAll(value, context.focusOnList);
      return Boolean(focusOnValues) && isIncluded(context.focusOnList, focusOnValues);
    },
    apply: (value, { dispatch, focusOnList }) => {
      if (isEmptyFocusOn(value)) {
        dispatch(paceActions.setPaceFocusOn([]));
        return;
      }
      const optionsSelected = getValuesFromList(expandFocusOnAll(value, focusOnList), focusOnList);
      dispatch(paceActions.setPaceFocusOn(optionsSelected?.length > 0 ? optionsSelected : []));
    },
  },
  {
    command: consoleCommands.COMPARE_WINDOW,
    event: events.SET_PACE_COMPARE_WINDOW,
    ready: (context) => context.isLoaded && Boolean(context.dateComparison),
    apply: (value, { customBooking, handleComparisonChange }) => {
      if (value) {
        setCompareWindowCommandValue(value, customBooking, handleComparisonChange);
      }
    },
  },
  {
    command: consoleCommands.FORECAST,
    event: events.SET_PACE_FORECAST,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(paceActions.setIsPaceForecast(value === 1));
    },
  },
  {
    command: consoleCommands.PERCENTAGE,
    event: events.SET_PACE_PERCENTAGE,
    ready: (context) => context.isLoaded && context.percentageList?.length > 0,
    apply: (value, { dispatch, percentageList }) => {
      const option = value ? percentageList?.filter((val) => val?.label?.includes(value)) : [];
      if (option?.length > 0) {
        dispatch(paceActions.setPercentage(option[0]?.id));
      }
    },
  },
  {
    command: consoleCommands.SEGMENT_BREAKDOWN,
    event: events.SET_PACE_SEGMENT_BREAKDOWN_TOGGLE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(paceActions.setIsPaceSegmentTimeDeAggregate(value === 1));
    },
  },
  {
    command: consoleCommands.TABULAR_VIEW,
    event: events.SET_PACE_TABULAR_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(paceActions.setTabularView(Number(value) === 1));
    },
  },
];

/**
 * Pace tab commands of a command url in the order they are applied
 */
const PACE_COMMAND_STEPS = [
  ...PACE_FILTER_COMMAND_STEPS,
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
  ...PACE_COLLAPSE_COMMAND_STEPS,
];

export default PACE_COMMAND_STEPS;
